/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { UseQueryResult } from '@tanstack/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import type { EDRInstance } from '@opengeoweb/shared';
import { fetchEDRCollectionDetails, fetchEDRParameter } from './api';
import { coverageCollectionToFeatureCollection } from './utils/coverageCollectionToFeatureCollection';

const DEFAULT_STALE_TIME = 60000; // One minute

// Ensures every webmap-react query starts with the same query key
const generateWebmapQueryKeys = (queryKeys: string[]): string[] =>
  ['webmap-react'].concat(queryKeys);

export const webmapEDRQueryOptions = {
  getCollectionDetailsQuery: (
    edrBaseUrlWithCollection: string,
    enabled: boolean,
    instanceId = '',
  ) => {
    return queryOptions({
      staleTime: DEFAULT_STALE_TIME,
      queryKey: generateWebmapQueryKeys([
        'get-collection-details',
        edrBaseUrlWithCollection,
        instanceId,
      ]),
      queryFn: async (): Promise<EDRInstance> =>
        fetchEDRCollectionDetails(edrBaseUrlWithCollection, instanceId),
      enabled: edrBaseUrlWithCollection.length > 0 && enabled,
    });
  },
} as const;

export const useEDRGetCollectionDetails = (
  edrBaseUrlWithCollection: string,
  instanceId = '',
  enabled = true,
): UseQueryResult<EDRInstance> => {
  return useQuery(
    webmapEDRQueryOptions.getCollectionDetailsQuery(
      edrBaseUrlWithCollection,
      enabled,
      instanceId,
    ),
  );
};

export const useEDRGetParameterData = (
  edrBaseUrlWithCollection: string,
  parameterName: string,
  datetime: string | null = '',
  instanceId = '',
): UseQueryResult<GeoJSON.FeatureCollection | undefined> => {
  const { data: collection } = useEDRGetCollectionDetails(
    edrBaseUrlWithCollection,
    instanceId,
  );

  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    queryKey: generateWebmapQueryKeys([
      'get-parameter-data',
      edrBaseUrlWithCollection,
      instanceId,
      parameterName,
      datetime ?? '',
    ]),
    queryFn: async (): Promise<GeoJSON.FeatureCollection | undefined> => {
      const data = await fetchEDRParameter(
        collection,
        edrBaseUrlWithCollection,
        parameterName,
        datetime ?? '',
        instanceId,
      );
      return coverageCollectionToFeatureCollection(data, parameterName);
    },
    enabled:
      collection !== undefined &&
      parameterName.length > 0 &&
      (datetime?.length ?? 0) > 0,
  });
};
